/**
 * Performance Optimization Configuration
 *
 * Centralized configuration for AR performance tuning parameters.
 * Used by marker selection, pose filtering, detection loop, and quality adaptation.
 *
 * @module config/performance
 */

/**
 * Detection timing configuration
 */
export const DETECTION_FPS_DEFAULT = 15; // Balanced CPU/responsiveness
export const DETECTION_FPS_HIGH = 12; // High-end devices (reduced from 20 for better FPS)
export const DETECTION_FPS_LOW = 10; // Low-end devices

/**
 * Marker selection configuration
 */
export const HYSTERESIS_THRESHOLD = 100; // pixels - Increased to 100 to prevent unwanted switching (was 40)
export const SELECTION_LOCK_DURATION = 500; // milliseconds - Reduced to 500ms to allow faster switching from edge (Tunnel Vision handles stability)

/**
 * Camera motion detection configuration
 */
export const CAMERA_STABILITY_DURATION = 300; // milliseconds - stability wait

/**
 * Pose filtering configuration (Kalman filter parameters)
 */
export const KALMAN_PROCESS_NOISE = 0.003; // q - Lower = less responsive, smoother (was 0.005)
export const KALMAN_MEASUREMENT_NOISE = 0.08; // r - Higher = trust measurements less (was 0.05)
export const MAX_SMOOTHING_FRAMES = 3; // Frame buffer limit for latency control
export const MAX_POSE_LATENCY = 100; // milliseconds - maximum acceptable latency

/**
 * Memory management configuration
 */
export const INACTIVE_MARKER_CLEANUP_DELAY = 30000; // 30 seconds - cleanup inactive markers
export const MEMORY_CHECK_INTERVAL = 5000; // 5 seconds - memory metrics update

/**
 * Quality tier presets
 */
export const QUALITY_PRESETS = {
  high: {
    tier: 'high',
    pixelRatio: null, // Use device pixel ratio (set at runtime)
    textureResolution: 1920, // 1080p video textures
    anisotropy: 16, // Maximum anisotropic filtering
    targetFps: 60,
    detectionFps: DETECTION_FPS_HIGH,
    enableObjectPooling: true,
    enableTextureOptimization: true,
    memoryCleanupInterval: 30, // seconds
  },
  mid: {
    tier: 'mid',
    pixelRatio: 2, // Cap at 2x for performance
    textureResolution: 1280, // 720p video textures
    anisotropy: 8, // Moderate anisotropic filtering
    targetFps: 30,
    detectionFps: DETECTION_FPS_DEFAULT,
    enableObjectPooling: true,
    enableTextureOptimization: true,
    memoryCleanupInterval: 30, // seconds
  },
  low: {
    tier: 'low',
    pixelRatio: 1, // No scaling for performance
    textureResolution: 854, // 480p video textures
    anisotropy: 4, // Minimal anisotropic filtering
    targetFps: 24,
    detectionFps: DETECTION_FPS_LOW,
    enableObjectPooling: true,
    enableTextureOptimization: true,
    memoryCleanupInterval: 20, // seconds - more aggressive cleanup
  },
};

/**
 * Performance monitoring configuration
 */
export const PERF_MONITORING = {
  fpsThreshold: 24, // FPS below this triggers degradation detection
  degradationDuration: 3000, // 3 seconds - sustained low FPS before fallback
  metricsUpdateInterval: 1000, // 1 second - metrics callback frequency
  memoryUpdateInterval: MEMORY_CHECK_INTERVAL,
};

/**
 * Fallback optimization steps (applied sequentially on performance degradation)
 */
export const FALLBACK_STEPS = [
  {
    name: 'Reduce Detection FPS',
    apply: (currentSettings) => ({
      ...currentSettings,
      detectionFps: Math.max(currentSettings.detectionFps - 5, DETECTION_FPS_LOW),
    }),
  },
  {
    name: 'Lower Texture Resolution',
    apply: (currentSettings) => ({
      ...currentSettings,
      textureResolution: Math.max(currentSettings.textureResolution * 0.66, 854),
    }),
  },
  {
    name: 'Reduce Pixel Ratio',
    apply: (currentSettings) => ({
      ...currentSettings,
      pixelRatio: Math.max((currentSettings.pixelRatio || 1) - 0.5, 1),
    }),
  },
  {
    name: 'Disable Anisotropic Filtering',
    apply: (currentSettings) => ({
      ...currentSettings,
      anisotropy: Math.max(currentSettings.anisotropy / 2, 0),
    }),
  },
];
